/* eslint-disable react/display-name */

import { useContext } from 'react';

// You can think of dynamic imports as another way to split your code into manageable chunks.
// More details on https://nextjs.org/docs/advanced-features/dynamic-import
import dynamic from 'next/dynamic';
import { PageContext } from '../../pages/render';
import { FC, PropsWithChildren } from 'react';
import { HtmlBlock, DangerouslySetHtmlBlock, BlockNode } from '@finder/ui-blocks.commons';
import { getPostContentComponent } from './postContent';

const Fragment: FC<{ index: number }> = ({
  children,
}: PropsWithChildren<Record<string, unknown>>) => {
  return <>{children}</>;
};

const TableImport = dynamic(() =>
  import('@finder/ui-blocks.layout/table' /* webpackChunkName: "ui-blocks.layout.table" */).then(
    ({ TableBlock }) => (props): JSX.Element => {
      return (
        <TableBlock
          postContent={getPostContentComponent}
          blocksMap={mappers}
          CellRenderer={BlockNode}
          {...props}
        />
      );
    }
  )
);

const TabsImport = dynamic(() =>
  import('@finder/ui-blocks.layout/tabs' /* webpackChunkName: "ui-blocks.layout.tabs" */).then(
    ({ TabsBlock }) => (props): JSX.Element => {
      return (
        <TabsBlock
          postContent={getPostContentComponent}
          blocksMap={mappers}
          CellRenderer={BlockNode}
          {...props}
        />
      );
    }
  )
);

export const mappers = {
  // eslint-disable-next-line react/display-name, @typescript-eslint/no-unused-vars
  'finder/fragment': Fragment,
  'finder/expander': dynamic(() =>
    import('@finder/ui-blocks.expander' /* webpackChunkName: "ui-blocks.expander" */).then(
      ({ ExpanderBlock }) => (props): JSX.Element => {
        require('@finder/ui-blocks.commons/dist/esm/commons.css');
        require('@finder/ui-blocks.expander/dist/esm/expander.css');
        return <ExpanderBlock {...props} />;
      }
    )
  ),
  'finder/grid': dynamic(() =>
    import(
      '@finder/ui-blocks.layout/grid' /* webpackChunkName: "ui-blocks.layout.grid" */
    ).then(({ Grid, applyGridDataTransformation }) => (props): JSX.Element => (
      <Grid {...applyGridDataTransformation(props)} />
    ))
  ),
  'finder/grid-column': dynamic(() =>
    import(
      '@finder/ui-blocks.layout/grid-column' /* webpackChunkName: "ui-blocks.layout.grid-column" */
    ).then(({ Col, applyColDataTransformation }) => (props): JSX.Element => (
      <Col {...applyColDataTransformation(props)} />
    ))
  ),
  'finder/accordion': dynamic(() =>
    import(
      '@finder/ui-blocks.layout/accordion' /* webpackChunkName: "ui-blocks.layout.accordion" */
    ).then(({ Accordion, applyAccordionDataTransformation }) => (props): JSX.Element => (
      <Accordion {...applyAccordionDataTransformation(props)} />
    ))
  ),
  'finder/hide-content': dynamic(() =>
    import(
      '@finder/ui-blocks.layout/hide-content' /* webpackChunkName: "ui-blocks.layout.hide-content" */
    ).then(({ HideContent }) => (props): JSX.Element => <HideContent {...props} />)
  ),
  'finder/wrapper': dynamic(() =>
    import(
      '@finder/ui-blocks.layout/wrapper' /* webpackChunkName: "ui-blocks.layout.wrapper" */
    ).then(({ WrapperBlock, applyWrapperDataTransformation }) => (props): JSX.Element => {
      require('@finder/ui-blocks.layout/dist/esm/layout.css');
      return <WrapperBlock {...applyWrapperDataTransformation(props)} />;
    })
  ),
  'finder/arrow-link': dynamic(() =>
    import(
      '@finder/ui-blocks.navigation/arrow-link' /* webpackChunkName: "ui-blocks.navigation.arrow-link" */
    ).then(({ ArrowLink }) => (props): JSX.Element => {
      return <ArrowLink {...props} />;
    })
  ),
  'finder/select-nav': dynamic(() =>
    import(
      '@finder/ui-blocks.navigation/select-navigation' /* webpackChunkName: "ui-blocks.navigation.select-navigation" */
    ).then(({ SelectNavigationBlock }) => (props): JSX.Element => {
      require('@finder/ui-blocks.navigation/dist/esm/navigation.css');
      return <SelectNavigationBlock {...props} />;
    })
  ),
  'finder/shopping': dynamic(() =>
    import('@finder/ui-blocks.shopping' /* webpackChunkName: "ui-blocks.shopping" */).then(
      ({ ShoppingBlock }) => (props): JSX.Element => {
        require('@finder/ui-blocks.shopping/dist/esm/shopping.css');
        const { isAdmin } = useContext(PageContext);
        return <ShoppingBlock isAdmin={isAdmin} {...props} />;
      }
    )
  ),
  'finder/table-of-contents': dynamic(() =>
    import(
      '@finder/ui-blocks.navigation/table-of-contents' /* webpackChunkName: "ui-blocks.navigation.table-of-contents" */
    ).then(({ TableOfContentsBlock }) => (props): JSX.Element => {
      require('@finder/ui-blocks.commons/dist/esm/commons.css');
      require('@finder/ui-blocks.navigation/dist/esm/navigation.css');
      const { headings } = useContext(PageContext);
      return <TableOfContentsBlock headings={headings} {...props} />;
    })
  ),
  'finder/table': TableImport,
  'finder/table-row': dynamic(() =>
    import(
      '@finder/ui-blocks.layout/table-row' /* webpackChunkName: "ui-blocks.layout.table-row" */
    ).then(({ TableRow }) => (props): JSX.Element => {
      return <TableRow {...props} />;
    })
  ),
  'finder/table-cell': dynamic(() =>
    import(
      '@finder/ui-blocks.layout/table-cell' /* webpackChunkName: "ui-blocks.layout.table-cell" */
    ).then(({ TableCell }) => (props): JSX.Element => {
      return <TableCell {...props} />;
    })
  ),
  'finder/tile': dynamic(() =>
    import('@finder/ui-blocks.layout/tile' /* webpackChunkName: "ui-blocks.layout.tile" */).then(
      ({ TileBlock }) => (props): JSX.Element => {
        return <TileBlock {...props} />;
      }
    )
  ),
  'finder/tabs': TabsImport,
  'finder/tab': dynamic(() =>
    import('@finder/ui-blocks.layout/tab' /* webpackChunkName: "ui-blocks.layout.tab" */).then(
      ({ TabBlock }) => (props): JSX.Element => {
        return <TabBlock {...props} />;
      }
    )
  ),
  'finder/research-authority-box': dynamic(() =>
    import(
      '@finder/ui-blocks.eats/research-authority-box' /* webpackChunkName: "ui-blocks.eats.research-authority-box" */
    ).then(({ ResearchAuthorityBoxBlock }) => (props): JSX.Element => {
      require('@finder/ui-blocks.eats/dist/esm/eats.css');
      return <ResearchAuthorityBoxBlock {...props} />;
    })
  ),
  'finder/methodology': dynamic(() =>
    import(
      '@finder/ui-blocks.eats/methodology' /* webpackChunkName: "ui-blocks.eats.methodology" */
    ).then(({ MethodologyBlock }) => (props): JSX.Element => {
      require('@finder/ui-blocks.eats/dist/esm/eats.css');
      return <MethodologyBlock {...props} />;
    })
  ),
  'finder/infogram': dynamic(() =>
    import(
      '@finder/ui-blocks.eats/infogram' /* webpackChunkName: "ui-blocks.eats.infogram" */
    ).then(({ InfogramBlock }) => (props): JSX.Element => {
      return <InfogramBlock {...props} />;
    })
  ),
  'finder/disclaimer': dynamic(() =>
    import(
      `@finder/ui-blocks.eats/disclaimer` /* webpackChunkName: "ui-blocks.eats.disclaimer" */
    ).then(({ DisclaimerBlock }) => (props): JSX.Element => {
      require('@finder/ui-blocks.eats/dist/esm/eats.css');
      return <DisclaimerBlock {...props} />;
    })
  ),
  'finder/nb-product': DangerouslySetHtmlBlock,
  'core/paragraph': HtmlBlock,
  'core/html': HtmlBlock,
  'core/group': dynamic(() =>
    import(`@finder/ui-blocks.layout/group` /* webpackChunkName: "ui-blocks.layout.group" */).then(
      ({ GroupBlock }) => (props): JSX.Element => {
        return <GroupBlock {...props} />;
      }
    )
  ),
};

export default mappers;
